import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal, ActivityIndicator, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { withAlpha } from '../theme';

interface ContactsExportDialogProps {
  visible: boolean;
  onClose: () => void;
  /** Count of contacts actively selected right now (selection mode) — 0
   * means "export everything" is the only real choice, so the "current
   * selection" option is hidden entirely rather than shown disabled. */
  selectedCount: number;
  onDownloadTemplate: () => void;
  onDownloadSelection: () => void;
  exporting: boolean;
}

/**
 * "Download template" vs "download contacts" (current selection when one is
 * active, else everything) — the export button's entry choice. The actual
 * contacts export applies permission-based masking server-side (see
 * OmniContactController.exportContacts), same as Reports; the template is
 * just column headers + an example row, same shape the old client-side
 * button already produced.
 */
export const ContactsExportDialog: React.FC<ContactsExportDialogProps> = ({
  visible,
  onClose,
  selectedCount,
  onDownloadTemplate,
  onDownloadSelection,
  exporting,
}) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <TouchableOpacity style={styles.overlay} activeOpacity={1} onPress={onClose}>
        <TouchableOpacity activeOpacity={1} onPress={(e) => e.stopPropagation()} style={styles.sheet}>
          <Text style={[styles.title, { textAlign: rtl.textStart }]}>{t('contactsScreen.export.title')}</Text>

          <TouchableOpacity style={[styles.option, { flexDirection: rtl.rowDirection }]} onPress={onDownloadTemplate} disabled={exporting}>
            <View style={styles.optionIcon}>
              <Ionicons name="document-outline" size={20} color={colors.primary} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={[styles.optionTitle, { textAlign: rtl.textStart }]}>{t('contactsScreen.export.templateTitle')}</Text>
              <Text style={[styles.optionSubtitle, { textAlign: rtl.textStart }]}>{t('contactsScreen.export.templateSubtitle')}</Text>
            </View>
          </TouchableOpacity>

          <TouchableOpacity style={[styles.option, { flexDirection: rtl.rowDirection }]} onPress={onDownloadSelection} disabled={exporting}>
            <View style={styles.optionIcon}>
              {exporting ? <ActivityIndicator size="small" color={colors.primary} /> : <Ionicons name="people-outline" size={20} color={colors.primary} />}
            </View>
            <View style={{ flex: 1 }}>
              <Text style={[styles.optionTitle, { textAlign: rtl.textStart }]}>
                {selectedCount > 0 ? t('contactsScreen.export.selectionTitle', { count: selectedCount }) : t('contactsScreen.export.allTitle')}
              </Text>
              <Text style={[styles.optionSubtitle, { textAlign: rtl.textStart }]}>{t('contactsScreen.export.selectionSubtitle')}</Text>
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
};

const getStyles = () => StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
  },
  sheet: {
    width: '100%',
    maxWidth: 380,
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    padding: spacing.lg,
    ...designTokens.shadows.md,
  },
  title: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.bold,
    color: colors.textDark,
    marginBottom: spacing.md,
  },
  option: {
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    marginBottom: spacing.sm,
  },
  optionIcon: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: withAlpha(colors.primary, 0.06),
    alignItems: 'center',
    justifyContent: 'center',
  },
  optionTitle: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.textDark,
  },
  optionSubtitle: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
    marginTop: 2,
  },
});
